import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useAccountInfo } from 'shared-modules/hooks';
import { useApOrManualInstrumentOptions } from 'shared-modules/hooks/symbol';
import { CFD, ETF, FX } from 'shared-modules/constants';
import { sendNotificationError } from 'shared-modules/redux/actions';
import { useChangeService } from 'shared-modules/services/hooks';
import { makeNotificationErrorParams } from 'shared-modules/utils/service';
import styles from './instrumentSelection.module.scss';
import { CfdInstrumentIcon, CustomFlag, EtfInstrumentIcon, Tabs } from '../../../../../components';

// TODO move to under SideMenu
const InstrumentSelection = ({ serviceId: initialServiceId, visible, onChange, onMouseEnter, onMouseLeave }) => {
  const dispatch = useDispatch();
  const [serviceId, setServiceId] = useState(initialServiceId);
  const instrumentOptions = useApOrManualInstrumentOptions(false, serviceId);
  const memoizedOptions = useMemo(() => instrumentOptions, [instrumentOptions]);
  const tradeServiceData = useChangeService();
  const accountInfo = useAccountInfo();
  const disabled = !!makeNotificationErrorParams({ accountInfo, serviceId });
  const [tabWidth, setTabWidth] = useState(344 / ((tradeServiceData.options?.length ?? 0) + 1));
  const handleResize = useCallback(
    ({ width }) => {
      const length = tradeServiceData.options?.length ?? 0;
      if (length > 0) {
        setTabWidth((width ?? 0) / length);
      }
    },
    [tradeServiceData.options],
  );

  useEffect(() => {
    // reset
    if (!visible) {
      setServiceId(initialServiceId);
    }
  }, [visible, initialServiceId]);

  const handleServiceId = (id) => {
    const errorParams = makeNotificationErrorParams({ accountInfo, serviceId: id });
    if (errorParams) {
      dispatch(sendNotificationError(errorParams));
    } else {
      setServiceId(id);
    }
  };

  const renderCurrencyItems = useMemo(() => {
    return memoizedOptions[serviceId].map((currency) => {
      const { label, value } = currency || {};
      const [firstFlag, secondFlag] = label?.split('/') || [];
      const content = () => {
        if (serviceId === FX) {
          return (
            <>
              <CustomFlag className={styles.icon} country={firstFlag} />
              <CustomFlag className={styles.icon} country={secondFlag} />
            </>
          );
        }
        if (serviceId === ETF) {
          return <EtfInstrumentIcon className={styles.icon} instrumentId={value} size="medium" />;
        }
        if (serviceId === CFD) {
          return <CfdInstrumentIcon className={styles.icon} instrumentId={value} size="medium" />;
        }
        return null;
      };

      const handleClick = () => {
        if (!disabled) {
          onChange({ serviceId, currency });
        }
      };

      return (
        <div
          key={value}
          role="button"
          onClick={handleClick}
          tabIndex={0}
          className={styles.instrumentSelectionButton}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleClick();
            }
          }}
        >
          {content()}
          <span className={styles.currency}>{label}</span>
        </div>
      );
    });
  }, [onChange, serviceId, memoizedOptions, disabled]);

  return visible ? (
    <div className={styles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className={styles.triangle} />
      <div className={styles.issueWrapper}>
        <div className={styles.tabWrapper}>
          <Tabs
            tabMinWidth={80}
            scrollWidth={tabWidth}
            containerClassName={styles.tabs}
            items={tradeServiceData.options}
            activeKey={serviceId}
            onChange={handleServiceId}
            onReady={handleResize}
            onResize={handleResize}
          />
        </div>
        <div className={styles.instrumentSelectionWrapper}>{renderCurrencyItems}</div>
      </div>
    </div>
  ) : null;
};

export default memo(InstrumentSelection);

InstrumentSelection.propTypes = {
  serviceId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};
